import { useAuth } from '@mdvip/shared/contexts/AuthContext';
import { trackException } from '@mdvip/shared/lib/telemetry';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

interface AuthCallbackPageProps {
  /** Path to navigate to after successful auth. Each portal supplies its own post-login landing route. */
  postLoginRedirect: string;
}

export function AuthCallbackPage({ postLoginRedirect }: AuthCallbackPageProps) {
  const { t } = useTranslation('shared');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { handleAuthCallback, initiateLogin } = useAuth();
  // canRetry gates the "return to sign in" CTA. A wrong_portal rejection must not
  // offer retry: re-login succeeds silently at SSO and is rejected again — a loop.
  const [authError, setAuthError] = useState<{ message: string; canRetry: boolean } | null>(null);
  // Keyed on the code value so Strict Mode double-invocation with the same code
  // is deduplicated, but a new code arriving after a failed exchange (mobile retry
  // via a fresh deep-link) is always allowed through.
  const lastAttemptedCodeRef = useRef<string | null>(null);

  useEffect(() => {
    // The backend redirects here with ?error= when login fails
    // (e.g. ?error=saml_failed&detail=... or ?error=wrong_portal). On native the
    // deep-link handler navigates here with the same query, so read the params
    // from the router — window.location isn't updated by a client-side navigate.
    const errorCode = searchParams.get('error');
    if (errorCode) {
      // wrong_portal = the role↔portal check rejected the user; SAML itself
      // succeeded. Show a localized message with no retry CTA (retry would loop).
      // A future enhancement can add a link to the user's correct portal.
      if (errorCode === 'wrong_portal') {
        setAuthError({ message: t('auth.wrongPortal'), canRetry: false });
        return;
      }
      const detail = searchParams.get('detail');
      setAuthError({
        message: detail ?? `Authentication failed (${errorCode}). Please try signing in again.`,
        canRetry: true,
      });
      return;
    }

    // The SAML IdP appends ?code= to the callbackUrl after authentication.
    const code = searchParams.get('code');

    if (!code) {
      setAuthError({ message: t('authCallback.noCode'), canRetry: true });
      return;
    }

    if (lastAttemptedCodeRef.current === code) return;
    lastAttemptedCodeRef.current = code;

    handleAuthCallback(code)
      .then(() => {
        // replace: true removes the callback URL from history so the back button
        // doesn't re-trigger the exchange with an already-consumed code.
        navigate(postLoginRedirect, { replace: true });
      })
      .catch((err) => {
        console.error('Auth callback failed:', err);
        if (err instanceof Error) trackException(err, { source: 'authCallback' });
        setAuthError({ message: t('authCallback.callbackFailed'), canRetry: true });
      });
  }, [handleAuthCallback, navigate, postLoginRedirect, searchParams, t]);

  if (authError) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-4">
        <div className="text-center">
          <p className="mb-4 text-sm text-destructive">{authError.message}</p>
          {authError.canRetry && (
            <button type="button" onClick={() => void initiateLogin()} className="text-sm text-primary underline">
              {t('authCallback.returnToSignIn')}
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <div className="flex flex-col items-center gap-3">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
        <p className="text-sm text-muted-foreground">{t('authCallback.completing')}</p>
      </div>
    </div>
  );
}
